import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import { Divider } from '#/components/Divider/Divider';
import type { FormUsageContext } from '#/components/Form/Form';
import { Form } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { GeneralError } from '#/components/Form/GeneralError';
import { InputField } from '#/components/Form/InputField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { InfoBox } from '#/components/InfoBox/InfoBox';
import { InviteMessage } from '#/components/InviteMessage/InviteMessage';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { AppleOAuthButton } from '#/components/OAuthButton/AppleOAuthButton';
import { FacebookOAuthButton } from '#/components/OAuthButton/FacebookOAuthButton';
import { GoogleOAuthButton } from '#/components/OAuthButton/GoogleOAuthButton';
import { BodySmall } from '#/components/Text/BodySmall';
import { useI18n } from '#/i18n/hooks/useI18n';

import {
  LoginForm_Box_1,
  LoginForm_Box_2,
  LoginForm_Box_3,
} from './LoginForm.styled';
import { LoginFormForgotPasswordLink } from './LoginFormForgotPasswordLink';
import { LoginFormSignupLink } from './LoginFormSignupLink';

export type LoginFormValues = {
  email: string;
  password: string;
  subscribeSubsiteId?: string;
};

type Props = {
  invite?: ApiInvite | null;
  analyticsTitle: string;
  usageContext: FormUsageContext;
  initialValues: LoginFormValues;
  thirdPartyLoginError: string | null | undefined;
  showSignupModal: (email: string) => void;
  getSignupUrl: (email: string) => {
    pathname: string;
    state: Record<string, unknown>;
  };
  signUpOverrideFn?: () => void;
  showForgotPasswordModal: (data: { email: string }) => void;
  getForgotPasswordUrl: (data: { email: string }) => {
    pathname: string;
    state: Record<string, unknown>;
  };
  getThirdPartyLoginUrl: (
    provider: 'facebook' | 'google' | 'apple',
  ) => Promise<string> | string;
  submitLogin: FormSubmitFunction<LoginFormValues>;
  centered?: boolean;
  initialWarning?: string | null | undefined;
  compactView?: boolean;
};

export function LoginForm({
  invite,
  analyticsTitle,
  usageContext,
  initialValues,
  thirdPartyLoginError,
  showSignupModal,
  getSignupUrl,
  signUpOverrideFn,
  showForgotPasswordModal,
  getForgotPasswordUrl,
  getThirdPartyLoginUrl,
  submitLogin,
  centered,
  initialWarning,
  compactView,
}: Props) {
  const { i18n } = useI18n();

  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      qaId="login-form"
      initialValues={initialValues}
      onSubmit={submitLogin}
      noScrollToError
      render={({ values: { email } }) => (
        <div style={{ textAlign: centered ? 'center' : 'left' }}>
          {invite && (
            <BodySmall style={{ marginTop: 3, marginBottom: 4 }}>
              {invite.hooks.map((hook) => (
                <InviteMessage
                  key={hook.hookType}
                  invite={invite}
                  hook={hook}
                />
              ))}
            </BodySmall>
          )}

          {initialWarning && (
            <InfoBox $type="warning" $marginTop={20} $marginBottom={20}>
              {initialWarning}
            </InfoBox>
          )}
          {thirdPartyLoginError && (
            <InfoBox className="error" $type="danger" $marginBottom={4}>
              {thirdPartyLoginError}
            </InfoBox>
          )}

          <LoginForm_Box_3>
            <BodySmall style={{ textAlign: 'center' }}>
              {i18n("Don't have an account?")}{' '}
              <LoginFormSignupLink
                email={email}
                getSignupUrl={getSignupUrl}
                signUpOverrideFn={signUpOverrideFn}
                showSignupModal={showSignupModal}
                formUsageContext={usageContext}
              >
                {i18n('Sign Up')}
              </LoginFormSignupLink>
            </BodySmall>
          </LoginForm_Box_3>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <GoogleOAuthButton getUrl={() => getThirdPartyLoginUrl('google')} />
            <FacebookOAuthButton
              getUrl={() => getThirdPartyLoginUrl('facebook')}
            />

            <AppleOAuthButton getUrl={() => getThirdPartyLoginUrl('apple')} />
          </div>

          {!compactView && <Divider marginTop={24} marginBottom={24} />}

          <GeneralError />

          <InputField
            name="email"
            type="email"
            dataModalFocus={usageContext === 'modal' ? 'true' : undefined}
            placeholder={i18n('casey.smith@example.com')}
            label={
              /* TRANSLATORS: Login form */
              i18n('Email')
            }
          />

          <InputField
            name="password"
            type="password"
            label={
              /* TRANSLATORS: Login form */
              i18n('Password')
            }
          />

          <LoginForm_Box_2>
            <SubmitButton fullWidth size="large">
              {
                /* TRANSLATORS: log in button */
                i18n('Log In')
              }
            </SubmitButton>
          </LoginForm_Box_2>

          <div style={{ marginTop: 16 }}>
            <BodySmall style={{ textAlign: 'center' }}>
              <div style={{ marginTop: '8px' }}>
                <LoginFormForgotPasswordLink
                  email={email}
                  formUsageContext={usageContext}
                  getForgotPasswordUrl={getForgotPasswordUrl}
                  showForgotPasswordModal={showForgotPasswordModal}
                >
                  {i18n('I forgot my password')}
                </LoginFormForgotPasswordLink>
              </div>
            </BodySmall>
          </div>

          <LoginForm_Box_1>
            {i18n(
              'By continuing, you agree to Idealist’s updated {{terms}}Terms of Service{{/terms}}, and acknowledge our {{privacy}}Privacy Policy{{/privacy}}.',
              {
                terms: (
                  <StyledNativeLinkWithRef
                    href={i18n('https://www.idealist.org/en/terms-of-service')}
                    target="_blank"
                  ></StyledNativeLinkWithRef>
                ),

                privacy: (
                  <StyledNativeLinkWithRef
                    href={i18n('https://www.idealist.org/en/privacy-policy')}
                    target="_blank"
                  ></StyledNativeLinkWithRef>
                ),
              },
            )}
          </LoginForm_Box_1>
        </div>
      )}
    />
  );
}
